/* src/features/5DD/SummaryView.js */
import React, { useState, useEffect } from 'react';
import { db } from '../../apiClient';
import { aiService } from '../../services/ai'; 
import { format, parseISO } from 'date-fns';

const styles = {
  container: { color: '#fff', padding: '10px', maxWidth: '800px', margin: '0 auto', fontFamily: 'serif' },
  backButton: { background: 'transparent', color: '#fff', border: '1px solid #fff', padding: '8px 12px', marginBottom: '15px', cursor: 'pointer', borderRadius:'4px', fontSize:'0.9rem' },
  
  paper: { 
    backgroundColor: '#f4e4bc', color: '#1a1a1a', padding: '20px', 
    borderRadius: '4px', boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
    marginBottom: '40px'
  },
  
  header: { textAlign: 'center', borderBottom: '2px solid #1a1a1a', paddingBottom: '20px', marginBottom: '30px' },
  title: { fontSize: '1.8em', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: '1px', lineHeight:'1.2' },
  meta: { fontSize: '0.95em', fontStyle: 'italic', color: '#555' },
  
  narrativeSection: { lineHeight: '1.6', fontSize: '1.05em', marginBottom: '40px', padding: '0 5px' },
  narrativeHeader: { fontSize: '1.2em', fontWeight: 'bold', marginTop: '20px', textDecoration: 'underline', color: '#2c3e50' },
  placeholderBox: { textAlign:'center', padding:'40px', border:'2px dashed #aaa', color:'#666', backgroundColor:'rgba(0,0,0,0.02)' },
  
  sectionTitle: { fontSize: '1.4em', borderBottom: '1px solid #aaa', paddingBottom: '5px', marginBottom: '15px', marginTop: '0' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '5px', marginBottom: '30px' },
  dayBox: { 
    border: '1px solid #1a1a1a', padding: '10px 2px', textAlign: 'center',
    backgroundColor: 'rgba(0,0,0,0.05)', fontSize: '0.8em'
  },
  statLabel: { display: 'block', fontSize: '0.8em', textTransform: 'uppercase', marginBottom:'5px' },
  statValue: { fontSize: '1.3em', fontWeight: 'bold' },

  nutSummaryCard: {
    backgroundColor: 'rgba(255,255,255,0.6)', border: '1px solid #ccc', borderRadius:'8px',
    padding: '15px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', justifyContent: 'space-around'
  },
  avgItem: { textAlign: 'center', minWidth: '60px' },
  avgLabel: { display: 'block', fontSize: '0.75em', textTransform: 'uppercase', color: '#555' },
  avgValue: { fontSize: '1.2em', fontWeight: 'bold', color: '#000' },
  
  nutList: { display: 'flex', flexDirection: 'column', gap: '0' },
  nutRow: { 
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    borderBottom: '1px solid #ccc', padding: '12px 0', fontSize: '0.9em'
  },
  nutDate: { fontWeight: 'bold', color: '#444', minWidth: '140px' },
  nutDetails: { display: 'flex', gap: '15px', justifyContent: 'flex-end', flex: 1, color: '#444' },
  nutMetric: { whiteSpace: 'nowrap' } 
};

// Styles for the Dev Tool area
const devStyles = {
    container: {
        marginTop: '20px', 
        paddingTop: '15px', 
        borderTop: '1px dashed #999', 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        gap: '10px'
    },
    label: { fontSize: '0.8em', color: '#666', fontStyle: 'italic', marginRight: '5px' },
    button: {
        padding: '8px 16px', 
        backgroundColor: '#4a4a4a', 
        color: '#fff',
        border: 'none', 
        borderRadius: '4px', 
        cursor: 'pointer', 
        fontSize: '0.9rem', 
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    }
};

function SummaryView({ session, courseId, profile, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [dailyTotals, setDailyTotals] = useState([]);

  const fetchSummary = async () => {
      setLoading(true);
      try {
        let course;
        
        if (courseId === 'current_virtual') {
            course = {
                start_date: profile.course_start_date,
                end_date: null,
                is_active: true,
                start_weight: profile.weight,
                end_weight: null,
                user_note: "Current Cycle",
                ai_summary: null,
                id: 'current_virtual'
            };
        } else {
            const history = await db.courses.history();
            course = (history || []).find(c => c.id === courseId) || null;
        }

        const queryEnd = course.is_active ? new Date().toISOString() : (course.end_date || new Date().toISOString());

        // Fetch Tasks
        const tasks = await db.tasks.list({ start_date: course.start_date, end_date: queryEnd });

        // Fetch Meals
        const meals = await db.meals.list({ start_date: course.start_date, end_date: queryEnd });

        setData({ course, tasks: tasks || [], meals: meals || [] });

        // Aggregate Meals
        const totalsMap = {};
        meals?.forEach(m => {
            const d = m.date_logged;
            if (!totalsMap[d]) {
                totalsMap[d] = { date: d, cal: 0, prot: 0, carb: 0, fat: 0, fib: 0 };
            }
            const nut = m.ai_analysis?.nutrition || {};
            totalsMap[d].cal += (nut.calories || 0);
            totalsMap[d].prot += (nut.protein_g || 0);
            totalsMap[d].carb += (nut.carbs_g || 0);
            totalsMap[d].fat += (nut.fat_g || 0);
            totalsMap[d].fib += (nut.fiber_g || 0);
        });
        const sortedTotals = Object.values(totalsMap).sort((a,b) => b.date.localeCompare(a.date));
        setDailyTotals(sortedTotals);

      } catch (error) {
        console.error("Error loading summary:", error);
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    if(courseId) fetchSummary();
  }, [courseId, session.user.id, profile]);


  const handleRunAnalysis = async () => {
      // Alert the user if they try to run this on a course that hasn't been synced to DB yet
      if (data.course.id === 'current_virtual') {
          alert("DEV NOTICE: This Active Course exists in your Profile but not in the 'user_courses' history table yet.\n\nPlease run the SQL fix to 'realize' this course so the AI can save the summary to it.");
          return;
      }
      
      const confirmMsg = data.course.ai_summary 
        ? "This will overwrite the existing narrative. Continue?" 
        : "Generate AI analysis for this course?";

      if (!window.confirm(confirmMsg)) return;

      setAnalyzing(true);
      try {
        const result = await aiService.generateCourseSummary(data.course.id, session.user.id);
        
        if (result.narrative) {
            setData(prev => ({
                ...prev,
                course: { ...prev.course, ai_summary: result.narrative }
            }));
        } else {
            alert("Analysis returned no text. Check Supabase logs.");
        }
      } catch (err) {
        alert(`Error: ${err.message}`);
      } finally {
        setAnalyzing(false);
      }
  };

  if (loading) return <div style={{color:'#fff', textAlign:'center', marginTop:'30px'}}>Loading Report...</div>;
  if (!data) return <div>No data found.</div>;

  const { course, tasks } = data;
  
  const firstName = profile?.full_name ? profile.full_name.split(' ')[0] : "My";
  const reportTitle = `${firstName}'s Five Days Different`;

  const getAvg = (key) => {
      const grandTotal = dailyTotals.reduce((acc, day) => acc + day[key], 0);
      return Math.round(grandTotal / 5);
  };

  const renderNarrative = () => {
      return (
          <div style={{ position: 'relative' }}>
              {course.ai_summary ? (
                  course.ai_summary.split('###').map((section, i) => {
                    if (!section.trim()) return null;
                    const [title, ...body] = section.split('\n');
                    return (
                        <div key={i} style={{marginBottom: '20px'}}>
                            <div style={styles.narrativeHeader}>{title}</div>
                            <p>{body.join(' ')}</p>
                        </div>
                    );
                })
              ) : (
                  <div style={styles.placeholderBox}>
                      <h3>Analysis Pending</h3>
                      <p>No narrative generated yet.</p>
                  </div>
              )}

              {/* UNCONDITIONALLY RENDER THE BUTTON CONTAINER */}
              <div style={devStyles.container}>
                  <span style={devStyles.label}>Developer Options:</span>
                  <button 
                    style={devStyles.button} 
                    onClick={handleRunAnalysis}
                    disabled={analyzing}
                  >
                    {analyzing ? "Synthesizing..." : (course.ai_summary ? "Regenerate / Overwrite" : "Run Analysis")}
                  </button>
              </div>
          </div>
      );
  };

  return (
    <div style={styles.container}>
      <button onClick={onBack} style={styles.backButton}>&larr; Back to History</button>
      
      <div style={styles.paper}>
        
        <div style={styles.header}>
            <h1 style={styles.title}>{reportTitle}</h1>
            <div style={styles.meta}>
                {course.user_note || (course.is_active ? "Current Cycle" : "Completed Cycle")} <br/>
                {format(parseISO(course.start_date), 'MMMM do, yyyy')} &mdash; {course.is_active ? 'In Progress' : 'Completed'}
            </div>
        </div>

        <div style={styles.narrativeSection}>{renderNarrative()}</div>

        <h3 style={styles.sectionTitle}>Practice Rhythm</h3>
        <div style={styles.grid}>
            {[0,1,2,3,4].map(i => {
                const dayTask = tasks && tasks[i]; 
                const sessions = dayTask ? [dayTask.completed_hh5_am, dayTask.completed_hh5_pm, dayTask.completed_hh5_night].filter(Boolean).length : 0;
                const hasData = !!dayTask;
                return (
                    <div key={i} style={{ ...styles.dayBox, backgroundColor: (sessions >= 3) ? 'rgba(76, 175, 80, 0.3)' : (hasData ? 'rgba(0,0,0,0.05)' : 'transparent') }}>
                        <span style={styles.statLabel}>Day {i+1}</span>
                        <span style={styles.statValue}>{hasData ? `${sessions}/3` : '-'}</span>
                    </div>
                );
            })}
        </div>

        <h3 style={styles.sectionTitle}>Nutritional Reset</h3>
        
        <div style={styles.nutSummaryCard}>
            <div style={styles.avgItem}><span style={styles.avgLabel}>Avg Cals</span><span style={styles.avgValue}>{getAvg('cal')}</span></div>
            <div style={styles.avgItem}><span style={styles.avgLabel}>Protein</span><span style={styles.avgValue}>{getAvg('prot')}g</span></div>
            <div style={styles.avgItem}><span style={styles.avgLabel}>Carbs</span><span style={styles.avgValue}>{getAvg('carb')}g</span></div>
            <div style={styles.avgItem}><span style={styles.avgLabel}>Fat</span><span style={styles.avgValue}>{getAvg('fat')}g</span></div>
            <div style={styles.avgItem}><span style={styles.avgLabel}>Fiber</span><span style={styles.avgValue}>{getAvg('fib')}g</span></div>
        </div>

        <div style={styles.nutList}>
            {dailyTotals.map((day, i) => (
                <div key={i} style={styles.nutRow}>
                    <div style={styles.nutDate}>{format(parseISO(day.date), 'EEEE, MMMM do')}</div>
                    
                    <div style={styles.nutDetails}>
                        <div style={styles.nutMetric}>{day.cal} Cal</div>
                        <div style={styles.nutMetric}>{Math.round(day.prot)}g P</div>
                        <div style={styles.nutMetric}>{Math.round(day.carb)}g C</div>
                        <div style={styles.nutMetric}>{Math.round(day.fat)}g F</div>
                        <div style={styles.nutMetric}>{Math.round(day.fib)}g Fib</div>
                    </div>
                </div>
            ))}
            {dailyTotals.length === 0 && <p style={{fontStyle:'italic'}}>No meals logged.</p>}
        </div>

      </div>
    </div>
  );
}

export default SummaryView;